import React, { useRef, useMemo } from 'react';
import './index.scss';
import { useColumnsFilter } from '~/hooks';
import { ChevronDown } from 'react-feather';
import { Popover } from 'react-tiny-popover';
import Pill from '../Pill';
import { IntlShape } from 'react-intl';
import {
    ColumnFilter as ColumnFilterInterface,
    ColumnValueOption,
    SortingParameters,
    onApplyFilter,
    resetFilterColumn,
} from '~/interfaces/columnValues/ColumnFilterContext.interface';
import FilterMenu from '~/components/Tables/FilterMenu';

export interface PillColumnFilterInterface {
    prefix: string;
    intl: IntlShape;
    resetFilterColumn: resetFilterColumn;
    columnFilters: ColumnFilterInterface[];
    onApplyFilter: onApplyFilter;
    columnValues: ColumnValueOption[];
    sortFieldName: string;
    headerName: string;
    columnName: string;
    placeholder: string;
    sortParams?: SortingParameters;
    disabled?: boolean;
}

const PillColumnFilter: React.FC<PillColumnFilterInterface> = ({
    prefix,
    intl,
    columnName,
    resetFilterColumn,
    columnFilters,
    onApplyFilter,
    columnValues = [],
    sortParams,
    sortFieldName,
    placeholder,
    disabled,
}) => {
    const menuRef = useRef();

    const columnDataFiltering = useColumnsFilter({
        columnName,
        resetFilterColumn,
        columnFilters,
        onApplyFilter,
        columnValues,
        sortParams: sortParams || {},
        sortFieldName,
    });

    const displayValue = useMemo(() => {
        const valuesCount = columnFilters.find(
            (col) => col.column === columnName
        )?.value.length;

        if (!valuesCount) {
            return placeholder;
        }

        return (
            valuesCount +
            ' ' +
            intl.formatMessage({
                id: valuesCount !== 1 ? 'values' : 'value',
            })
        );
    }, [columnFilters]);

    const { filterMenu } = columnDataFiltering;

    return (
        <div ref={menuRef}>
            <Popover
                isOpen={filterMenu.showMenu}
                positions={['bottom', 'left', 'right']}
                align="start"
                content={
                    <FilterMenu
                        {...columnDataFiltering}
                        isSortable={Boolean(sortParams)}
                        intl={intl}
                    />
                }
                onClickOutside={() => filterMenu.setShowMenu(false)}
                parentElement={menuRef.current}
            >
                <div
                    className={`pill-column-filter__header-body ${disabled ? 'pill-column-filter__disabled' : ''}`}
                >
                    <Pill
                        onClick={() => !disabled && filterMenu.handleMenu()}
                        clickable
                    >
                        <div className="pill-column-filter__content">
                            {prefix && (
                                <span className="pill-column-filter__prefix">
                                    {prefix}:{' '}
                                </span>
                            )}
                            <span className="pill-column-filter__placeholder">
                                {displayValue}
                            </span>
                        </div>
                        <ChevronDown />
                    </Pill>
                </div>
            </Popover>
        </div>
    );
};

export default PillColumnFilter;
